import { useEffect, useRef, useState } from 'react';

import { usePolling } from '@/hooks/usePolling';

/** Lightweight summary of a reasoning step, surfaced to the chat UI for the
 * "agent thinking" indicator. The progress endpoint returns full step records;
 * we only need the most recent stage and its parsed content for display. */
export interface ReasoningStepSummary {
  stage: string;
  content: string;
  step_number: number;
  is_final: boolean;
}

interface UseProposalPollingOptions {
  onComplete: () => void;
  onError: (error: Error) => void;
}

interface ProgressStep {
  stage?: unknown;
  content?: unknown;
  step_number?: unknown;
  is_final?: unknown;
}

interface ProgressData {
  status?: string;
  complete?: boolean;
  chain_status?: string;
  partial_steps?: ProgressStep[];
  // Celery-progress fields. The backend writes status text like
  // "Starting reasoning process…" / "Processing step 3…" / "✓ Updated…"
  // every time progress advances. The shape is sometimes flat
  // (`description`) and sometimes nested (`progress.description`) — we
  // accept both because both are produced by `celery-progress` in the
  // wild and the codebase reads both in ReasoningChat.tsx.
  description?: unknown;
  progress?: { description?: unknown };
}

function extractProgressDescription(data: ProgressData): string | undefined {
  const flat = data.description;
  if (typeof flat === 'string' && flat.trim().length > 0) {
    return flat;
  }
  const nested = data.progress?.description;
  if (typeof nested === 'string' && nested.trim().length > 0) {
    return nested;
  }
  return undefined;
}

function extractLatestStep(
  data: ProgressData
): ReasoningStepSummary | undefined {
  const partialSteps = data.partial_steps;
  if (!Array.isArray(partialSteps) || partialSteps.length === 0) {
    return undefined;
  }
  const last = partialSteps[partialSteps.length - 1];
  if (!last || typeof last.content !== 'string') {
    return undefined;
  }
  return {
    stage: typeof last.stage === 'string' ? last.stage : '',
    content: last.content,
    step_number: typeof last.step_number === 'number' ? last.step_number : 0,
    is_final: !!last.is_final,
  };
}

/**
 * Specialized polling hook for proposal discussion conversations.
 * Wraps the base usePolling hook with proposal-specific completion logic.
 *
 * Also tracks the most recent completed reasoning step so the chat UI can
 * surface what the agent is working on while the user waits.
 *
 * @param taskId - The task ID from the discuss API response
 * @param conversationId - The conversation ID as string
 * @param orgId - Optional organization ID
 * @param options - Callbacks for completion and error handling
 */
export function useProposalPolling(
  taskId: string | null,
  conversationId: string | null,
  orgId: string | undefined,
  options: UseProposalPollingOptions
) {
  const { onComplete, onError } = options;
  const [latestStep, setLatestStep] = useState<ReasoningStepSummary | null>(
    null
  );
  // Celery-progress description — updated every poll well before the first
  // step row commits, so it's the right thing to show in the popover for
  // the first ~few seconds of every turn.
  const [progressDescription, setProgressDescription] = useState<string | null>(
    null
  );

  // Reset the cached state ONLY when a new task starts — not when the
  // current task ends. After completion we want `latestStep` to persist
  // so the AgentResponsePanel can display whatever the agent produced.
  // The progress description, by contrast, is a transient running-task
  // status and should clear immediately.
  const previousTaskIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (taskId && taskId !== previousTaskIdRef.current) {
      setLatestStep(null);
      setProgressDescription(null);
    }
    previousTaskIdRef.current = taskId;
  }, [taskId]);

  const { isPolling } = usePolling(taskId, conversationId, orgId, {
    enabled: !!taskId && !!conversationId,
    interval: 1000,
    onUpdate: data => {
      const progress = data as ProgressData;
      const next = extractLatestStep(progress);
      if (next) {
        setLatestStep(prev => {
          // Skip the state update if nothing changed — prevents a re-render
          // every poll tick when the agent hasn't completed a new step yet.
          if (
            prev &&
            prev.step_number === next.step_number &&
            prev.content === next.content &&
            prev.stage === next.stage
          ) {
            return prev;
          }
          return next;
        });
      }

      const nextDescription = extractProgressDescription(progress);
      if (nextDescription !== undefined) {
        setProgressDescription(prev =>
          prev === nextDescription ? prev : nextDescription
        );
      }

      // Check if reasoning is complete — distinguish success from failure
      // so the UI can show an error toast for failed chains instead of
      // silently swallowing the failure.
      const isFailed = progress.chain_status === 'failed';
      const isComplete =
        progress.status === 'completed' ||
        progress.complete === true ||
        progress.chain_status === 'completed' ||
        progress.chain_status === 'paused' ||
        isFailed;

      if (isComplete) {
        // Keep `latestStep` so the AgentResponsePanel can show what the
        // agent produced. It's reset above when a new task starts.
        setProgressDescription(null);
        if (isFailed) {
          onError(new Error('Something went wrong processing your message.'));
        } else {
          onComplete();
        }
      }
    },
    onError: errorMessage => {
      onError(new Error(errorMessage));
    },
  });

  return { isPolling, latestStep, progressDescription };
}
